/* Every user-visible string, keyed, in one file.
 *
 * The standing rule is that small print lives in one content file rather than
 * hard-coded across components, and the same reasoning applies to the whole
 * surface: this product ships in two languages and one of them is
 * right-to-left, so a string typed into JSX is a string that will never be
 * translated and never be found again.
 *
 * ARABIC THAT DOES NOT EXIST IS NULL, NOT INVENTED
 * The design bundle carries approved Arabic for the consumer app and for
 * exactly one counter state. Everywhere else Arabic is `null`, the renderer
 * falls back to English, and `missingArabic()` lists what is outstanding.
 * Machine-translating a refusal into Arabic and shipping it as approved copy
 * is the same category of thing as a sample card in an empty wallet.
 *
 * AND ARABIC IS A COMPETENCE PROBLEM, NOT A TRANSLATION PROBLEM
 * A 120B model reads "كل يومين تقريباً" (roughly every two days) as
 * `fortnightly`, five times out of five, where the identical English is right
 * five times out of five. Every Arabic string in this product was produced or
 * will be produced by a model, and we now have direct evidence that a large
 * one fails on a phrase a child would parse.
 *
 * So Arabic has two states here and they are different things:
 *
 *   `S(en, ar)`      the bundle's own Arabic, verbatim. `copy-check.mjs`
 *                    asserts every one of these appears in an artboard, so
 *                    "it came from the bundle" is a fact rather than a claim.
 *   `DRAFT(en, ar)`  Arabic written in this repo. Reported by
 *                    `unreviewedArabic()` and fatal at ship time until a
 *                    native speaker has read it.
 *
 * That distinction was not free. `code.title` sat in `S()` looking approved
 * for weeks: "أظهري هذا عند الكاشير" is nowhere in the bundle, and the bundle's
 * own counter copy uses اعرضي rather than أظهري for the same instruction. It
 * was assembled here from two nearby phrases. The provenance check found it.
 *
 * COPY THAT IS MINE IS DECLARED, NOT ASSUMED APPROVED
 * Most strings here come from the design bundle. Some do not, because the
 * screen they belong to has no artboard. Those are listed in
 * `PENDING_APPROVAL` and reported by `pendingApproval()`, on the same
 * principle as the Arabic: the gap is written down rather than left to be
 * discovered by a customer. Not a blocker on building; a blocker on shipping,
 * and `scripts/copy-check.mjs` is where that becomes true.
 *
 * NO EM-DASHES. The house rule, applied here because this is where the copy
 * actually lives.
 */
export type Lang = "en" | "ar";

/**
 * Whether a native speaker has read this Arabic. THERE IS NO DEFAULT.
 *
 * The previous shape was `drafted: false` as a default argument, so any Arabic
 * added through the ordinary helper asserted it had been reviewed -- by nobody,
 * silently, because the author never typed the word. Twenty-six strings carried
 * that assertion and one of them says "send me a code" in the feminine while
 * another three screens later says "verify and continue" in the masculine.
 *
 * A control that depends on the author remembering to fail is not a control.
 *
 * Inverting the default would not have fixed it -- an inverted default is still
 * a default, and the next helper someone writes re-invents the old one. So the
 * argument is required and has no default value at all: an entry carrying
 * Arabic cannot be constructed without saying, in the call, who read it.
 */
export type Review =
  | { readonly state: "drafted" }
  | { readonly state: "reviewed"; readonly by: string; readonly on: string };

/** Nobody has read this yet. */
export const DRAFTED: Review = { state: "drafted" };

/** A named person read it on a named day. Both are required; neither is inferable. */
export const reviewedBy = (by: string, on: string): Review => ({ state: "reviewed", by, on });

/** Where the Arabic came from, which is a different question from who read it. */
export const CUSTOMER_APP = "Damga Customer App.dc.html";
export const COUNTER_AND_WALLET = "Damga V1 Counter and Wallet.dc.html";
export const WRITTEN_HERE = "written here, not from the design bundle";

type Entry = {
  en: string;
  ar: string | null;
  /** null when there is no Arabic. There is nothing to review. */
  review: Review | null;
  source: string | null;
};

/**
 * English only.
 *
 * ONE ARGUMENT, deliberately. This helper cannot carry Arabic at all, so the
 * path that used to let an unreviewed string in without anyone noticing does
 * not exist any more -- it is a compile error rather than a convention.
 */
const S = (en: string): Entry => ({ en, ar: null, review: null, source: null });

/** English and Arabic. The review state and the provenance are both required. */
const AR = (en: string, ar: string, review: Review, source: string): Entry =>
  ({ en, ar, review, source });

export const COPY = {
  // --- the first screen, from the consumer app's own string tables ----------
  //
  // The artboard's chooser and its intro are one screen and these are its
  // words, with three of its buttons deliberately absent. See Welcome.tsx for
  // which and why: the tabs are a false choice, email was ruled recovery
  // rather than a way in, and Microsoft is in no ruling.
  "welcome.title": AR("Your cards are waiting.", "بطاقاتك تنتظرك.", DRAFTED, CUSTOMER_APP),
  // THE SECOND SENTENCE IS GONE, and it was ruled false twice before it went.
  // "They all reach the same cards" is not true: a Google sign-in carries no
  // phone number, so GoTrue mints a second account and a customer who signed
  // up one way and returns another finds an empty wallet. The first sentence
  // is still good advice and it is all this line now says.
  "welcome.body": AR(
    "Use whichever method you signed up with.",
    "اختاري الطريقة التي أنشأتِ بها حسابك.",
    DRAFTED, CUSTOMER_APP,
  ),
  // What the same screen says while the mobile number is the only route in.
  // Not a placeholder for the sentence above: it makes the promise that
  // matters, which is that stamps collected before signing up are not lost.
  "welcome.bodySingle": S(
    "If you have stamped anywhere before, every card you hold is already waiting.",
  ),
  "welcome.continuePhone": S("Continue with mobile number"),
  "welcome.continueEmail": S("Continue with email"),
  // THREE PARTS, BECAUSE ONE OF THEM IS A LINK, and "the terms" is gone
  // because no terms page exists. A sentence naming a document the reader
  // cannot open is worse than not naming it; it comes back the day there is a
  // page to link. The privacy notice exists at damga.qa/privacy/ and is linked.
  // The agreement wording itself is unchanged: that is the lawyer's sentence.
  "welcome.terms.lead": S("By continuing you agree to the"),
  "welcome.terms.link": S("privacy notice"),
  "welcome.terms.tail": S(". Damga never sells your data and no merchant can see your other cards."),
  "welcome.noApp": S("No app to download. Add it to your home screen."),
  "welcome.everyBranch": S("Your cards work at every branch of every shop."),
  // "YOU CAN DELETE IT ANY TIME" NAMED A CAPABILITY THE READER DOES NOT HAVE.
  // It reads as a control on this screen, or one screen away. There is no
  // delete screen: `DELETE /v1/me` has no UI reaching it, and the erase.*
  // strings below are drafted for a screen nobody has built. The privacy notice
  // now names exactly one working route, and this sentence names the same one,
  // because a reassurance that points somewhere the notice does not is how the
  // two drift apart. When the screen ships, this goes back to the short form.
  "welcome.yourData": S("Your data is yours. Email hello@damga.qa and we delete it."),

  // --- sign-in, from the consumer app's own string tables ------------------
  "auth.title": AR("Your cards live on your number.", "بطاقاتك مرتبطة برقمك.", DRAFTED, CUSTOMER_APP),
  "auth.body": AR(
    "Sign in with your mobile. If you have stamped anywhere before, every card you hold is already waiting.",
    "سجّلي الدخول برقم جوالك. إن جمعتِ أختاماً من قبل، فكل بطاقاتك في انتظارك.",
    DRAFTED, CUSTOMER_APP,
  ),
  "auth.mobile": AR("Mobile number", "رقم الجوال", DRAFTED, CUSTOMER_APP),
  "auth.send": AR("Send me a code", "أرسلي لي الرمز", DRAFTED, CUSTOMER_APP),
  // "WE SEND ONE SMS TO VERIFY THE NUMBER" WAS UNTRUE ON A LIVE SURFACE.
  //
  // No SMS provider is configured -- `twilio_account_sid` was empty in the
  // Doha environment and the blocks are now gone from it entirely, because
  // Twilio has no Middle East region and does not guarantee data stays in the
  // one you pick. Nothing sends. A screen promising a message that never
  // arrives is worse than one that promises nothing, because the customer
  // waits for it.
  //
  // The Arabic went with the English: it said the same thing, and a
  // translation of a false sentence is a false sentence.
  "auth.consent": S(
    "Damga never sells your data and no merchant sees your other cards.",
  ),
  "auth.otpTitle": AR("Enter the six digits.", "أدخلي الأرقام الستة.", DRAFTED, CUSTOMER_APP),
  "auth.changeNumber": AR("Change number", "تغيير الرقم", DRAFTED, CUSTOMER_APP),
  "auth.verify": AR("Verify and continue", "تحقّق ومتابعة", DRAFTED, CUSTOMER_APP),
  "auth.badNumber": S("That does not look like a Qatari mobile number."),
  // --- email sign-in, the channel that needs nobody's approval --------------
  //
  // Email was ruled recovery rather than a way in, and the ruling assumed a
  // phone path existed. It does not, so this is a way in until one does. The
  // copy does not apologise for it: a customer does not need to know which of
  // our four gatekeepers is currently closed.
  "auth.emailTitle": S("Your cards live on your email."),
  "auth.emailBody": S(
    "We send a six-digit code. If you have stamped anywhere before, every card you hold is already waiting.",
  ),
  "auth.email": S("Email"),
  "auth.changeEmail": S("Change email"),
  "auth.badEmail": S("That does not look like an email address."),
  "auth.emailConsent": S(
    "We send one email to verify the address. Damga never sells your data and no merchant sees your other cards.",
  ),
  "auth.badCode": S("That code did not work. Check the digits and try again."),
  // Distinct from a refusal on purpose. This one is about the connection, so
  // it tells the person the one thing they can act on.
  "auth.unreachable": S(
    "Could not reach Damga. Check the connection and try again.",
  ),

  // --- the chooser, restored to the artboard --------------------------------
  //
  // THESE WERE CUT AND THE RULING WAS WITHDRAWN. Welcome.tsx used to explain
  // why the tabs, email, Apple, Google and Microsoft were absent. That
  // reasoning assumed a phone path existed; none does, because every messaging
  // channel we have sits in somebody else's approval queue. The three provider
  // routes are the only ones we can switch on unilaterally, so they stopped
  // being redundant and became the product's only unblocked front door.
  //
  // Arabic here is the bundle's own, verbatim, so the provenance check sources
  // every one of them. The English drops the artboard's em-dash for the house
  // rule, which is the only place these differ from what was drawn.
  "auth.tabSignIn": AR("Sign in", "تسجيل الدخول", DRAFTED, CUSTOMER_APP),
  "auth.tabSignUp": AR("Create account", "حساب جديد", DRAFTED, CUSTOMER_APP),
  // The signed-out title changes with the tab, because the two states really
  // are making different promises: one says your things are already here, the
  // other says this will not take long.
  "auth.chooserTitleUp": AR(
    "Start collecting in a minute.",
    "ابدئي بجمع الأختام والنقاط.",
    DRAFTED, CUSTOMER_APP,
  ),
  // THE SECOND SENTENCE PROMISED A FEATURE THAT DOES NOT EXIST. Nothing in the
  // app links a second sign-in method to an account -- no linkIdentity call,
  // no Settings screen for it -- and it is the same false promise as "they all
  // reach the same cards", made on the sign-up side of the same screen. The
  // Arabic is cut to its first sentence, which is still a substring of the
  // artboard's string, so the copy gate still finds it in the source.
  "auth.chooserBodyUp": AR(
    "Pick how you want to sign up.",
    "اختاري طريقة إنشاء الحساب.",
    DRAFTED, CUSTOMER_APP,
  ),
  "auth.withApple": AR("Continue with Apple", "المتابعة بـ Apple", DRAFTED, CUSTOMER_APP),
  "auth.withGoogle": AR("Continue with Google", "المتابعة بـ Google", DRAFTED, CUSTOMER_APP),
  "auth.withMicrosoft": AR("Continue with Microsoft", "المتابعة بـ Microsoft", DRAFTED, CUSTOMER_APP),
  "auth.or": AR("or", "أو", DRAFTED, CUSTOMER_APP),
  "auth.withMobile": AR(
    "Continue with mobile number",
    "المتابعة برقم الجوال",
    DRAFTED, CUSTOMER_APP,
  ),
  "auth.withEmail": AR(
    "Continue with email",
    "المتابعة بالبريد الإلكتروني",
    DRAFTED, CUSTOMER_APP,
  ),
  "auth.allOptions": AR("All sign-in options", "كل طرق الدخول", DRAFTED, CUSTOMER_APP),

  // --- when a provider does not finish --------------------------------------
  //
  // NO ARTBOARD DRAWS THESE, because the bundle drew the provider buttons as
  // buttons that simply work. Three separate sentences rather than one, because
  // they are three different situations and only one of them is our fault.
  "auth.providerCancelled": S(
    "That sign-in was cancelled. Nothing was created, and you can pick another way in.",
  ),
  // The customer approved us and this device could not finish, which is a
  // different thing from a refusal and must not read as one.
  "auth.providerLost": S(
    "We lost track of that sign-in on the way back. Start it again from this screen.",
  ),
  "auth.providerFailed": S(
    "That sign-in did not complete. Try again, or use your mobile number.",
  ),

  // --- my code (screen B1) --------------------------------------------------
  // DRAFT, and it was in S() until the provenance check ran. This exact
  // sentence is in no artboard; it was assembled from "عند الكاشير" and a verb
  // that is not the one the bundle uses. The bundle's own counter line is
  // "اعرضي رمزك عند الكاشير قبل الدفع", so اعرضي is very likely the right verb
  // here too, which is a suggestion for the reviewer and not a change to make
  // on my own authority.
  "code.title": AR("Show this at the counter", "أظهري هذا عند الكاشير", DRAFTED, WRITTEN_HERE),
  "code.rotates": S("New code in {seconds}s"),
  "code.works_offline": S(
    "Works without a connection. Read the six digits to the person serving you.",
  ),
  "code.thisCard": S("This card"),

  // --- wallet (screens B2, B4) ---------------------------------------------
  "wallet.title": AR("Wallet", "المحفظة", DRAFTED, CUSTOMER_APP),
  // The heading over the cards that are not ready yet. It was an English
  // literal sitting in the middle of a screen where every other sentence came
  // from this table, so it could never have been translated and nothing would
  // have reported it missing.
  "wallet.almostThere": S("Almost there"),
  // THE RULING: the customer is told the date when the reward is issued and
  // sees it on the reward. No reminder message in v1 -- push is unbuilt and
  // messaging costs money -- so this line is the only warning they get, which
  // is the argument for it being a date rather than a vague "soon".
  "wallet.useBy": S("Use by {date}"),

  // --- profile and settings -------------------------------------------------
  //
  // Four rows, because the test for each was whether a customer at a coffee
  // shop ever opens this screen to do that thing. Language and "delete my
  // data" are absent and Settings.tsx records why: one would ship Arabic no
  // native speaker has read, the other is a promise the first screen makes and
  // the API cannot keep.
  "settings.title": S("You"),
  "settings.name": S("Name"),
  "settings.phone": S("Mobile"),
  "settings.email": S("Email for recovery"),
  // Said plainly rather than hidden behind a tick. An address nobody has
  // confirmed helps a person find you; it is not a recovery path yet, and the
  // row should not imply that it is.
  "settings.emailUnverified": S("Added, not confirmed"),
  "settings.faceId": S("Passkey"),
  "settings.notSet": S("Not set"),
  "settings.add": S("Add"),
  "settings.change": S("Change"),
  "settings.setUp": S("Set up"),
  "settings.on": S("On"),
  "settings.off": S("Off"),
  "settings.signOut": S("Sign out"),
  // The cost is stated because the customer is the one who pays it in time and
  // we are the ones who pay it in riyals. It used to be a one-tap link in the
  // corner of every screen with no warning at all.
  "settings.signOutNote": S(
    "You will need to sign in again.",
  ),
  // THE SAME SENTENCE IS FALSE ONCE A PASSKEY EXISTS, and it is false in the
  // direction that costs us: a customer told signing out will cost them a text
  // message and a wait does not sign out, and the one who has already made
  // signing back in free is the one most safely told so. The whole point of
  // the passkey is that this stops being a one-way door.
  // --- erasure --------------------------------------------------------------
  //
  // AWAITING THE FOUNDER'S REVIEW. The endpoint is built and tested; the screen
  // is not, deliberately, because this is the copy that decides whether a
  // person understands what they are agreeing to and it should be read before
  // it is rendered.
  //
  // THE HARD PART IS THE SECOND HALF. Erasure here is real but not total: a
  // merchant's record of visits survives with no name attached, the sign-in
  // service still holds the phone number, and backups hold the row for
  // fourteen days. Every one of those is defensible and none of them is what a
  // person assumes "delete my data" means. So the screen says them, in plain
  // words, before the button rather than after it.
  //
  // Written to avoid two failures. It does not reassure ("Don't worry, your
  // data is safe"), which is the wrong sentence on a screen about deletion.
  // And it does not hedge into legalese, which reads as hiding something.
  "erase.title": S("Delete your data"),
  "erase.body": S(
    "This removes your name, your number, your email and your code. It cannot be undone, and your stamps go with it.",
  ),
  // THE CONCRETE LOSS, WITH THEIR ACTUAL NUMBERS. "Your stamps go with it" is
  // abstract, and abstraction is what lets somebody tap through something
  // irreversible. A person deciding this deserves the figure, not the
  // category.
  //
  // Composed from two pluralised fragments rather than written as one
  // sentence, because the two counts pluralise independently -- "1 stamp at 3
  // shops" and "8 stamps at 1 shop" both happen -- and a single template
  // cannot get both right. It also survives Arabic, which has six plural
  // categories where English has two.
  "erase.stampCount.one": S("{n} stamp"),
  "erase.stampCount.other": S("{n} stamps"),
  "erase.stampCount.zero": S("{n} stamps"),
  "erase.stampCount.two": S("{n} stamps"),
  "erase.stampCount.few": S("{n} stamps"),
  "erase.stampCount.many": S("{n} stamps"),
  "erase.shopCount.one": S("{n} shop"),
  "erase.shopCount.other": S("{n} shops"),
  "erase.shopCount.zero": S("{n} shops"),
  "erase.shopCount.two": S("{n} shops"),
  "erase.shopCount.few": S("{n} shops"),
  "erase.shopCount.many": S("{n} shops"),
  "erase.tally": S(
    "You have {stamps} at {shops}. They will be closed and cannot be recovered.",
  ),

  "erase.keepsTitle": S("What stays, and why"),
  // Named individually. "Some data is retained" makes a person assume the
  // worst about all of it.
  //
  // TWO ITEMS, NOT THREE. The sign-in service used to keep the number and the
  // line saying so has been deleted rather than reworded, because the number
  // is now genuinely removed. Wording a limitation as a benefit is how the
  // counter's calm grey "Saved" happened.
  "erase.keepsShops": S(
    "Each shop keeps a record of visits at their counter, with nothing on it that says who you were.",
  ),
  // THIRTY, NOT FOURTEEN, and the difference is which backup you mean. The
  // dump bucket has a locked 14-day retention; Cloud SQL keeps 30 nightly
  // automated backups of the same database, which is the longer and therefore
  // the true answer to "when is it really gone". The live privacy notice
  // already says 30. This said 14 and would have shipped the day the screen
  // was built, so it is corrected now rather than then.
  "erase.keepsBackups": S(
    "Backups are erased within 30 days. We keep them that long so a mistake on our side is fixable.",
  ),
  "erase.confirm": S("Delete everything"),
  // Names what is kept rather than saying "Cancel", and pluralised because a
  // person with five cards reading "Keep my card" wonders which one.
  "erase.cancel.one": S("Keep my card"),
  "erase.cancel.other": S("Keep my cards"),
  "erase.cancel.zero": S("Keep my cards"),
  "erase.cancel.two": S("Keep my cards"),
  "erase.cancel.few": S("Keep my cards"),
  "erase.cancel.many": S("Keep my cards"),
  "erase.done": S("Your data is deleted."),
  "erase.doneBody": S(
    "You can start again any time with the same number. It will be a new card.",
  ),

  "settings.signOutNotePasskey": S(
    "Your passkey will sign you back in. No code needed.",
  ),
  "wallet.empty.title": S("Nothing here yet"),
  "wallet.empty.body": S(
    "Your first card arrives the moment someone stamps you. Show your code at any Damga counter.",
  ),
  "wallet.showCode": S("Show my code"),
  "wallet.refresh": S("Check for new stamps"),
  "wallet.readyToClaim": AR("Ready to claim", "جاهزة للاستلام", DRAFTED, CUSTOMER_APP),
  "wallet.progress": S("{balance} of {threshold}"),
  // Plural forms are keyed by CLDR category, not by an `n === 1` check.
  // English needs two; Arabic needs six, and a hand-rolled ternary would make
  // the Arabic copy impossible to write correctly when it arrives.
  "wallet.count.one": S("{n} card"),
  "wallet.count.other": S("{n} cards"),
  "wallet.count.zero": S("{n} cards"),
  "wallet.count.two": S("{n} cards"),
  "wallet.count.few": S("{n} cards"),
  "wallet.count.many": S("{n} cards"),

  // --- activity ------------------------------------------------------------
  //
  // OURS, NOT THE BUNDLE'S, AND PENDING FOUNDER APPROVAL. The consumer
  // artboard renders only its sign-in state and its string table carries the
  // tab label and nothing else, so there is no approved copy for a ledger
  // entry and none has been invented to look like there is. Written to two
  // rules from the spec: nothing implies the customer did something wrong, and
  // anything that took value back says so plainly and gives them somewhere to
  // go.
  //
  // This is the screen that tells someone a stamp was taken back, which is the
  // most delicate sentence in the product, and it has been read by nobody but
  // its author. Declared in PENDING_APPROVAL below; see copy-check.mjs.
  "activity.title": AR("Activity", "النشاط", DRAFTED, CUSTOMER_APP),
  "activity.empty.title": S("Nothing yet"),
  "activity.failed": S(
    "We could not load your activity just now. Your cards are unaffected.",
  ),
  "activity.empty.body": S(
    "Every stamp, reward and correction shows up here, across all your cards.",
  ),

  "activity.EARN": S("Stamped"),
  "activity.IMPORT_OPENING": S("Card brought over"),
  "activity.IMPORT_OPENING.detail": S("The shop added what you had already collected."),
  "activity.REWARD_ISSUE": S("Reward earned"),
  "activity.REWARD_ISSUE.voided": S("Reward taken back"),
  "activity.REWARD_ISSUE.voidedDetail": S("{reward}. The stamp that finished the card was undone, so the reward went with it."),
  "activity.REWARD_USE": S("Reward claimed"),
  "activity.REWARD_USE_REVERSAL": S("Reward returned to your card"),
  "activity.EXPIRY": S("Points expired"),

  // The artboard's own words for this, from screen A5: "She is told her card
  // was corrected."
  "activity.EARN_REVERSAL": S("Card corrected"),
  "activity.EARN_REVERSAL.byStaff": S(
    "A stamp from {when} was taken back by the shop.",
  ),
  "activity.EARN_REVERSAL.plain": S("A stamp from {when} was taken back."),
  "activity.EARN_REVERSAL.withReward": S("A stamp from {when} was taken back by the shop, and the reward it finished went with it."),

  // They were told at the counter that it was saved. It was not.
  "activity.SYNC_REJECTED": S("A stamp could not be confirmed"),
  "activity.SYNC_REJECTED.E_TOO_SOON": S("It came too soon after your last one."),
  "activity.SYNC_REJECTED.E_MEMBER_CAP": S("That day was already full for this card."),
  "activity.SYNC_REJECTED.other": S("The shop could not confirm it when they came back online."),
  "activity.SYNC_REJECTED.ask": S("Ask the shop if you think this is wrong."),

  // --- a card whose merchant is closing -------------------------------------
  // MINE. There is no artboard for a closing card: B5 draws the aftermath and
  // nothing draws the notice period. Written to the one thing the spec is
  // emphatic about, which is that nobody is surprised, and to gap 32, which
  // says what a customer is owed at that moment is a contract question and not
  // a screen. So these state what happened and when, and promise nothing.
  "card.closing.label": S("Closing"),
  "card.closing.stopped": S("This shop is leaving Damga. No new stamps."),
  "card.closing.claimBy": S("Claim your reward before {date}."),
  "card.closing.until": S("The card closes on {date}."),

  // --- archive (screen B5) --------------------------------------------------
  // B5's own words, with one change: its body line carries an em-dash, which
  // the house rule forbids, so the clause was split rather than copied. See
  // design-copy-drift.md.
  "archive.link.one": S("1 quieter card lives in Archive"),
  "archive.link.other": S("{n} quieter cards live in Archive"),
  "archive.title": S("Quiet cards"),
  "archive.body": S(
    "Not used in a while. Everything you earned is still here. One stamp brings a card back to the top.",
  ),
  "archive.footer": S(
    "Nothing is deleted from Archive. Points expire on the merchant's rule; stamps do not.",
  ),
  "archive.closed": S("Shop no longer on Damga"),
  // MINE. B5 shows an Archive holding dormant cards and one closed shop.
  // Dormancy is not implemented, so today it holds closed shops only, and
  // "Quiet cards / not used in a while" would be describing something that is
  // not on the screen. The artboard's words are above and get used the moment
  // they are true.
  "archive.closedOnly.title": S("Shops that closed"),
  "archive.closedOnly.body": S(
    "These shops have left Damga. Nothing you earned has been deleted.",
  ),
  "archive.empty.title": S("Nothing in Archive"),
  "archive.empty.body": S(
    "Cards you stop using, and shops that leave Damga, are kept here.",
  ),
  "archive.back": S("Back to wallet"),
  "archive.points": S("{n} pts"),

  // A stamp that appears in the past, in a ledger we tell people is
  // append-only. Nothing is wrong and it looks wrong, which is exactly the
  // state that needs a sentence: not an apology, a fact that makes the
  // appearance legible.
  "activity.recordedLate": S("Recorded late · this is from {when}"),

  "activity.points": S("{n} points"),
  "activity.stamp": S("1 stamp"),

  // --- the app shell, rebuilt from the artboard (2026-09-17) ----------------
  //
  // `Damga Customer App.dc.html`, the light register. Every line here was
  // checked against the code before it was written; what the artboard said and
  // the product does not do is cut, not softened. The decisions are in
  // reports/surfaces/customer-app.md, "The build, decided".
  "shell.tab.home": S("Home"),
  "shell.tab.settings": S("Settings"),
  "shell.eyebrow.morning": S("Good morning"),
  "shell.eyebrow.afternoon": S("Good afternoon"),
  "shell.eyebrow.evening": S("Good evening"),
  "shell.eyebrow.wallet": S("Stamps and points"),
  "shell.eyebrow.activity": S("Your history"),
  "shell.eyebrow.settings": S("Preferences"),
  "shell.title.home": S("Your cards"),
  // The artboard's "+" (join a shop) and "Updates" were here. There is no
  // customer-side join and no notification channel. The code is the one thing
  // a customer needs from any screen, at a counter.
  "shell.code": S("Code"),
  "shell.notNow": S("Not now"),

  "home.stat.cards": S("cards"),
  "home.stat.ready": S("to claim"),
  // Visits the API counts: EARN entries no reversal took back. Not "stamps",
  // which a reward resets, and not the artboard's "saved QAR": nothing has a
  // riyal value.
  "home.stat.visits": S("visits"),
  "home.closest": S("Closest to a reward"),
  "home.allCards": S("All cards"),
  "home.recent": S("Recent"),
  "home.allActivity": S("All activity"),

  "detail.back": S("Wallet"),
  "detail.redeem": S("Redeem this reward"),
  "detail.howTo": S("How to earn"),
  // min_gap_minutes is per programme version, the same number E_TOO_SOON uses.
  "detail.howTo.stamps": S("Read your six digits at the counter. One stamp a visit."),
  "detail.howTo.points": S("Read your six digits at the counter. Each visit adds {n} points."),
  "detail.howTo.gap": S("At most one every {minutes} minutes."),
  "detail.reward": S("The reward"),
  // True because REWARD_ISSUE subtracts the threshold: the card starts again,
  // carrying anything over.
  "detail.reward.repeats": S("Every {threshold} on this card. The card starts again after each one."),
  "detail.reward.lasts": S("Lasts {days} days once earned."),
  "detail.reward.keeps": S("Does not lapse once earned."),
  "detail.fact.visits": S("visits"),
  "detail.fact.since": S("member since"),
  "detail.fact.ready": S("ready to claim"),
  "detail.fact.last": S("last stamp"),
  "detail.yourCode": S("Your code"),
  // THE SAME CODE, SAID ON THE CARD (2026-09-18). The founder saw six digits and
  // could not work out how they reach the right card; on a card, unlabelled, the
  // code reads as that shop's, and the first time it works at another shop the
  // product contradicts its own screen. The code names the person; the till names
  // the shop.
  "detail.sameEverywhere": S("The same six digits at every shop"),
  "detail.progress.stamps": S("{balance} of {threshold} stamps"),
  "detail.progress.points": S("{balance} of {threshold} points"),

  "sheet.code.eyebrow": S("At the counter"),
  // redemption.py refuses without the customer's code and refuses a code that
  // is not this card's owner. So the sentence is exactly what happens.
  "sheet.redeem.body": S(
    "Read these six digits to the cashier. They key them in and the reward comes off your card.",
  ),
  "sheet.account": S("Your account"),
  "sheet.careful": S("Careful"),

  "seal.stamped": S("Stamped"),
  "seal.ready": S("Ready"),
  "seal.saved": S("Saved"),
  "toast.stamped": S("{shop}: stamped. {progress}."),
  "toast.ready": S("{shop}: {reward} is ready to claim."),
  "toast.nameSaved": S("Name saved."),
  "toast.emailSaved": S("Email saved."),
  "toast.passkeyOn": S("Passkey on. Next time, no code."),

  "settings.security": S("Sign-in and security"),
  "settings.yourData": S("Your data"),
  "settings.whoSees": S("Who sees what"),
  "settings.whoSeesBody": S("Exactly what a shop can see about you."),
  "settings.delete": S("Delete your account"),
  "settings.deleteBody": S("Erases your name, number, email and code."),
  "settings.noName": S("Add your name"),

  // Verified 2026-09-17: the earn success body carries the full display name
  // and mask_identity's last four digits; search_members is scoped to one
  // business and masked; no merchant route returns another shop's card.
  "who.body": S(
    "When a shop stamps you, their counter shows the name you gave, the last four digits of your number and your balance with them.",
  ),
  "who.search": S(
    "Staff searching for you see a shortened name, and only among their own customers.",
  ),
  "who.visits": S("They keep a record of your visits at their counter."),
  "who.notOthers": S("They cannot see your other cards."),

  "signout.title": S("Sign out of Damga?"),
  "signout.body": S("Your cards stay on your account. Sign back in the same way and they are all here."),
  "signout.cards": S("Cards held"),
  "signout.as": S("Signed in as"),

  // --- the screens the artboard drew empty, brought back 2026-09-17 ---------
  //
  // Ruled the same day: a screen that is EMPTY ships, a screen that is WRONG
  // does not. These are empty. Each says what is true today without pretending
  // the thing it is empty of is coming.
  "shell.tab.profile": S("Profile"),
  "shell.updates": S("Updates"),

  "wallet.switch.mine": S("My wallet"),
  "wallet.switch.favs": S("Favourites"),
  "wallet.switch.discover": S("Discover"),

  "favs.empty.title": S("Nothing saved yet"),
  // The artboard says "any programme or shop". No shop can be saved, because
  // none is listed, so this says what a person can actually do.
  "favs.empty.body": S("Tap the heart on a card and it lands here, so the ones you use most are first."),
  "favs.add": S("Save {name}"),
  "favs.remove": S("Remove {name}"),
  "favs.saved": S("{name} saved."),
  "favs.removed": S("{name} removed."),

  "discover.empty.title": S("No shops listed yet"),
  "discover.empty.body": S("When shops are listed on Damga, they appear here."),
  // True today and the reason the tab is not a dead end: a card comes into
  // being at the first stamp, wherever that is.
  "discover.empty.code": S("You can still collect anywhere that uses Damga. Show your code."),

  "updates.title": S("What you have missed"),
  "updates.empty.title": S("Nothing new"),
  // Every notice below is worked out on the phone from the cards it already
  // has. Damga sends nothing: there is no channel, and this does not imply one.
  "updates.empty.body": S("When a reward is ready, or a card is close to one, it shows here."),
  "updates.note": S("Worked out on your phone from your own cards. Damga sends you nothing."),
  "updates.markRead": S("Mark all read"),
  "notice.ready": S("{reward} is ready to claim at {shop}."),
  "notice.close": S("{n} more at {shop} for {reward}."),
  "notice.useBy": S("Use your reward at {shop} by {date}."),
  "notice.closing": S("{shop} is leaving Damga."),

  "profile.standing": S("Your standing"),
  "profile.standingBody": S("Damga keeps every stamp you earn, across every shop you have joined, in one place."),
  "profile.rewardCount.one": S("{n} reward taken"),
  "profile.rewardCount.other": S("{n} rewards taken"),
  "profile.rewardCount.zero": S("{n} rewards taken"),
  "profile.rewardCount.two": S("{n} rewards taken"),
  "profile.rewardCount.few": S("{n} rewards taken"),
  "profile.rewardCount.many": S("{n} rewards taken"),
  "profile.acrossShops.one": S("across {n} shop"),
  "profile.acrossShops.other": S("across {n} shops"),
  "profile.acrossShops.zero": S("across {n} shops"),
  "profile.acrossShops.two": S("across {n} shops"),
  "profile.acrossShops.few": S("across {n} shops"),
  "profile.acrossShops.many": S("across {n} shops"),
  "profile.row.cards": S("Your cards"),
  "profile.row.cardsBody": S("Balances, progress and your code"),
  "profile.row.activity": S("Your activity"),
  "profile.row.activityBody": S("Every stamp, reward and correction"),
  "profile.stat.rewards": S("rewards taken"),

  "settings.export": S("Download your data"),
  "settings.exportBody": S("Every card, stamp and reward, as a file."),
  "export.title": S("Download your data"),
  // What the route actually returns, said plainly. No email, no hour's wait:
  // the file is made when the button is pressed.
  "export.body": S("A file of every card, stamp, reward and consent Damga holds for you, made now."),
  "export.note": S("Your sign-in details and the secret behind your code are not in it. They are credentials, not records."),
  "export.confirm": S("Download"),
  "export.failed": S("That did not download. Try again."),

  "home.step.done": S("Done"),
  "wallet.readyCount.one": S("{n} reward ready"),
  "wallet.readyCount.other": S("{n} rewards ready"),
  "wallet.readyCount.zero": S("{n} rewards ready"),
  "wallet.readyCount.two": S("{n} rewards ready"),
  "wallet.readyCount.few": S("{n} rewards ready"),
  "wallet.readyCount.many": S("{n} rewards ready"),
  "time.today": S("Today"),
  "time.yesterday": S("Yesterday"),
  "time.daysAgo": S("{n} days ago"),
  "activity.group.today": S("Today"),
  "activity.group.yesterday": S("Yesterday"),
  "activity.group.week": S("Earlier this week"),
  "activity.group.earlier": S("Earlier"),

  // --- the counter, screens A1 to A7 ---------------------------------------
  // --- enrolling somebody who has no app ------------------------------------
  //
  // THE CONSENT SENTENCE IS THE PRODUCT OF THIS SCREEN, not decoration on it.
  // Qatar's PDPPL does not recognise implied consent and requires a record of
  // how and when it was obtained; a cashier remembering they said something is
  // not a record. So this exact string is what the cashier says aloud, what
  // the screen shows, and what is written into `consent_events.wording` -- one
  // string, three places, because two records that disagree are worse than one
  // that is imperfect.
  //
  // It is also the string the printed counter card carries, generated from
  // here rather than typed there. The card outlives every deploy, so a
  // sentence on it that stopped being true is the worst kind of untrue.
  //
  // WHAT IT DOES NOT SAY, and both were struck deliberately:
  //   "We'll text you your stamps" -- there is no SMS sender ID registered
  //     with Ooredoo or Vodafone, nothing has been filed, and approval runs
  //     weeks after a clean submission. It is not true today.
  //   "Your number stays in Qatar" -- true today because nothing sends, and
  //     false the moment a provider is switched on. A laminated card outlives
  //     a configuration change.
  "enrol.consent": S(
    "We save your stamps to this number so you do not need a card. You can ask "
    + "us to delete it at any time.",
  ),
  "enrol.title": S("New customer"),
  "enrol.hint": S("Their mobile number. Say the line below before they give it."),
  "enrol.placeholder": S("Mobile number"),
  "enrol.nameLabel": S("Name"),
  "enrol.nameHint": S("Optional. It is what they see on their card."),
  "enrol.agreed": S("They said yes"),
  "enrol.submit": S("Enrol and stamp"),
  "enrol.needConsent": S("They have to agree before you can add them."),
  "enrol.done": S("{name} is on the card. One of {threshold}."),
  /* AN EMAIL AT THE TILL (047), and not for sending mail. The address lets a
   * card made at this counter find the account the customer signs in with,
   * because SMS sign-in is parked and a Google sign-in otherwise makes an
   * account nothing connects to the card. Nothing is ever sent to it.
   *
   * `enrolEmail.consent` MUST EQUAL THE WORDING IN consent_versions, channel
   * card_email, to the character: the server refuses to keep an address under
   * any other sentence. Change one and the other in the same commit.
   *
   * The read-back is the control. A mistyped address bounces nothing -- no mail
   * is sent -- it silently gives the card to whoever owns the typo, so the
   * cashier reads it back and the customer says it is right before anything is
   * saved. */
  // --- the merchant's own sign-in, on the till's kiosk (2026-09-18) ------------
  // The username and password Damga issued; the code after it is the second
  // factor on a new device, never a way in by itself. English until the sheet.
  "merchantSignIn.title": S("Sign in to the till"),
  "merchantSignIn.body": S("With the username Damga or your shop's owner gave you, and the password you chose."),
  "merchantSignIn.username": S("Username"),
  "merchantSignIn.password": S("Password"),
  "merchantSignIn.show": S("Show"),
  "merchantSignIn.hide": S("Hide"),
  "merchantSignIn.submit": S("Sign in"),
  "merchantSignIn.working": S("One moment"),
  "merchantSignIn.refused": S("That username and password do not match, or this sign-in is paused for fifteen minutes after too many tries."),
  "merchantSignIn.codeTitle": S("Check your email"),
  "merchantSignIn.codeBody": S("This tablet is new to your sign-in, so we have emailed you six digits. They work once, for ten minutes."),
  "merchantSignIn.codeLabel": S("The six digits"),
  "merchantSignIn.verify": S("Verify and continue"),
  "merchantSignIn.codeRefused": S("That code did not work. It may have expired or been used; start again for a new one."),
  "merchantSignIn.codeIncomplete": S("Enter all six digits."),
  "merchantSignIn.startAgain": S("Start again"),
  "merchantSignIn.tooManyCodes": S("Five codes have gone out in the last hour. Use the newest one, or wait a little."),
  "merchantSignIn.mailDown": S("We could not send the email just now. Nothing has changed; try again in a minute."),
  "merchantSignIn.unreachable": S("Could not reach Damga. Check the connection and try again."),

  "enrolEmail.label": S("Email"),
  "enrolEmail.hint": S("Optional. So their card is there when they sign in to the Damga app with it."),
  "enrolEmail.consent": S(
    "We keep this email only so your card appears when you sign in to the Damga app with it. "
    + "We never send you anything.",
  ),
  "enrolEmail.agreed": S("They said yes to the email"),
  "enrolEmail.needConsent": S("They have to agree to the email line, or leave the email empty."),
  "enrolEmail.notValid": S("That is not a full email address."),
  "enrolEmail.readBack": S("Read it back to them"),
  "enrolEmail.readBackHint": S("Letter by letter. A wrong letter could give their card to a stranger."),
  "enrolEmail.right": S("They said it is right"),
  "enrolEmail.change": S("Change it"),
  "enrolEmail.kept": S("When they sign in to the Damga app with that email, the card is there."),
  "enrolEmail.existing_card": S(
    "The email was not saved: this number already had a card here, and an email only goes on a new card.",
  ),
  "enrolEmail.has_account": S("The email was not saved: they already have a Damga account, and the card is on it."),
  "enrolEmail.not_available": S("The email was not saved: that line has not been approved yet."),
  "enrolEmail.other": S("The email was not saved. The stamp was."),
  // CUT 2026-09-17: "enrol.claim" told the cashier to send the customer to
  // damga.qa to claim their stamps. It rendered nowhere, and it was untrue: the
  // landing has no way into the app, SMS sign-in is parked, and a Google
  // sign-in makes a second account that cannot see a till-enrolled card.
  "E_BAD_NUMBER.title": S("That is not a Qatari mobile"),
  "E_BAD_NUMBER.body": S("Check the digits and try again."),
  "E_CONSENT_REQUIRED.title": S("They have not agreed yet"),
  "E_CONSENT_REQUIRED.body": S("Say the line and tick the box before adding them."),

  // --- finding a customer without a code -----------------------------------
  //
  // Reached two ways, and they are different situations wearing one screen.
  // The code path failed -- a flat battery, a phone in a bag, no signal in a
  // basement -- or a code matched two people and the counter is deliberately
  // not told which. Neither is the customer's fault and none of this copy says
  // it is.
  //
  // No Arabic here, and that is not an oversight: there is none to mine. The
  // artboards carry exactly one Arabic string for the whole counter. Writing it
  // would be writing Arabic, which is the one thing this table refuses to do.
  "lookup.title": S("Find a customer"),
  "lookup.hint": S("Phone number or name. Two characters or more."),
  "lookup.placeholder": S("Phone or name"),
  "lookup.search": S("Search"),
  "lookup.searching": S("Looking"),
  "lookup.none": S("Nobody here matches that."),
  // CORRECTED 2026-09-18: "A first-time customer joins with their code" was
  // true of a customer with the app and read as a dead end for one without it,
  // at the very moment the cashier was enrolling one (the end-to-end test).
  "lookup.scope": S(
    "Customers of this shop only. Someone new is not here yet: with the app, take their code; without it, search their number and enrol them.",
  ),
  "lookup.card": S("{balance} of {threshold}"),
  "lookup.noCard": S("No card yet"),
  "lookup.stamp": S("Stamp this card"),
  "lookup.back": S("Back to the keypad"),
  "lookup.offline": S("Searching needs a connection. The keypad still works."),

  // The ambiguous-code state. It says how many and nothing about who, because
  // one of them is a stranger to this transaction and their partial name is
  // not the merchant's to see.
  "ambiguous.title": S("Two cards share that code"),
  "ambiguous.body": S(
    "It happens with six digits. Find them by phone or name and the same code "
    + "still counts.",
  ),
  "ambiguous.find": S("Find by phone"),

  // On the keypad itself, so it is somewhere a cashier can reach before
  // anything has gone wrong rather than only after.
  "counter.findCustomer": S("No code? Find by phone"),
  // The header's button on the kiosk. It was hard-coded English in the JSX
  // until the till became a component (2026-09-18); it lives here now.
  "counter.endShift": S("End shift"),

  "counter.enterCode": S("Enter the code"),
  "counter.stamp": S("Stamp"),
  "counter.nextCustomer": S("Next customer"),
  // Giving a reward (2026-09-18): nothing at the counter could, until now.
  "counter.giveReward": S("Give a reward"),
  "counter.giveEnter": S("Their code, for the reward"),
  "counter.giveBack": S("Back to stamping"),
  "counter.giveNow": S("Give the reward now"),
  "counter.giveNowWhat": S("Give it now: {reward}"),
  "counter.given": S("{reward}: hand it over"),
  "counter.givenPlain": S("The reward: hand it over"),
  "counter.givenBody": S("It is off their card, and the card starts again."),
  "counter.undoWithTimer": S("Undo · {clock} left"),
  // A1. The bundle's headline is "Stamped. Six to go." with the number spelled
  // out; a digit is used here because the count is computed and a spelled
  // number would need a words table in two languages to say the same thing.
  "counter.stamped": S("Stamped. {remaining} to go."),
  "counter.stampedComplete": S("Stamped. Reward earned."),
  "counter.rewardLine": S("{reward} at {threshold}"),

  // A2, the only counter state the bundle translates.
  "E_TOO_SOON.title": S("Too soon"),
  // ARABIC REMOVED, NOT CORRECTED, AND THAT IS THE SAFER STATE.
  //
  // The mined string was "تم منح الطابع قبل ٤٠ دقيقة. الطابع التالي في ٤:١٥ مساءً."
  // -- forty minutes and 4:15pm written into the words, because it came off an
  // artboard showing one RENDERED EXAMPLE rather than a template. Every
  // Arabic-speaking customer would have been told the same wrong time forever,
  // in a sentence that renders like any other.
  //
  // Nothing here can fix it: the correct string needs the two placeholders and
  // a native speaker, and it is on the review sheet. A missing string falls
  // back to English and is obvious; a wrong one is obvious only to the
  // customer reading it. `copy.test.ts` now fails the build on any Arabic
  // whose placeholders differ from its English, so the next mined string like
  // this cannot get in the same way.
  "E_TOO_SOON.body": S(
    "This card allows one stamp every {gap} minutes. Next available at {next}.",
  ),
  // VERIFIED-OR-CUT 2026-09-17: "it comes back in about an hour" was true only
  // for a sixty-minute gap. The gap is the shop's setting, so the line now says
  // the time the refusal itself carries.
  "E_TOO_SOON.say": S(
    "You are already stamped for this visit. The next one is available at {next}.",
  ),

  // A3. The bundle's line uses an em-dash; restructured into two sentences.
  "duplicate.title": S("Already counted"),
  // CORRECTED 2026-09-17: this said "She has not been stamped twice" about
  // every customer. The artboard's example was one woman; the template is
  // everyone, and a pronoun guessed from nothing is wrong about half of them.
  "duplicate.body": S(
    "Your first tap worked. {name} is on {balance} of {threshold} and has not been stamped twice.",
  ),

  "E_OWN_ACCOUNT.title": S("This is your own account"),
  "E_OWN_ACCOUNT.body": S(
    "Ask a colleague to stamp you. Staff cards are stamped by someone else, on any shift.",
  ),

  "E_CODE_UNKNOWN.title": S("That code did not match"),
  "E_CODE_UNKNOWN.body": S(
    "Ask for the code again. It changes every minute, so the one on screen now is the one to read.",
  ),
  // A code that matched more than one account (2026-09-17). Nothing about who;
  // the next code is independent, so asking again is the whole fix.
  // THE CONSENT GATE, SAID (2026-09-18). These refused at enrolment and had no
  // words, so the till showed "something went wrong, try once more", which is
  // false twice: nothing went wrong, and trying again cannot work. A first stamp
  // through the app now passes the same gate, so in production, until a lawyer
  // approves the sentence, every app customer's first stamp at a new shop lands
  // here. Existing cardholders are not affected, and the words say so.
  "E_CONSENT_VERSION_DRAFT.title": S("This shop cannot add new customers yet"),
  "E_CONSENT_VERSION_DRAFT.body": S("The sentence a new customer agrees to is still waiting for approval. Anyone who already has a card here is stamped as usual. Nothing was recorded."),
  "E_CONSENT_VERSION_MISSING.title": S("This shop cannot add new customers yet"),
  "E_CONSENT_VERSION_MISSING.body": S("There is no sentence on file for a new customer to agree to. Tell Damga. Anyone who already has a card here is stamped as usual. Nothing was recorded."),
  "E_CONSENT_VERSION_SYNTHETIC.title": S("This shop cannot add new customers yet"),
  "E_CONSENT_VERSION_SYNTHETIC.body": S("The sentence on file is test wording, which works only on staging. Tell Damga. Nothing was recorded."),

  "E_CODE_AMBIGUOUS.title": S("Ask for the next code"),
  // VERIFIED-OR-CUT 2026-09-17: "and the next one will not" is cut. The next
  // code is independent of this one, so it collides with the same small chance
  // (earn.py's figures), and a promise that it will not is untrue.
  "E_CODE_AMBIGUOUS.body": S("This one matched more than one card. It changes every minute."),
  // A code already accepted for this customer at this shop in this minute
  // (earn.py, the spent-code burn). Rendered as the generic "something went
  // wrong, try once more" until 2026-09-17, and trying once more with the same
  // code can only be refused again.
  "E_CODE_SPENT.title": S("That code was just used"),
  "E_CODE_SPENT.body": S("Ask for the next code. It changes every minute."),
  "E_CODE_EXPIRED.title": S("That code has expired"),
  "E_CODE_EXPIRED.body": S("Ask for the current one. It changes every minute."),

  "E_MEMBER_CAP.title": S("Already stamped today"),
  "E_MEMBER_CAP.body": S("This card allows {cap} stamps a day here, and today is full."),

  // REWRITTEN, because the words described behaviour that no longer exists.
  // "Ask a colleague to take the next few" is a routine lunch-rush limit, and
  // the velocity cap stopped refusing anything -- it is an alert now, and this
  // code is reachable only above an absurd ceiling and only online, kept so a
  // genuinely compromised login can be stopped without cutting a release. The
  // copy has to describe that, or the first person to see it will look for a
  // limit to raise. See 026_the_cap_is_an_alert.sql.
  "E_STAFF_CAP.title": S("This till is locked"),
  "E_STAFF_CAP.body": S(
    "Damga has stopped it, not the shop. Call the owner before serving anyone else.",
  ),

  "E_PROGRAM_INACTIVE.title": S("This programme is not running"),
  "E_PROGRAM_INACTIVE.body": S("Nothing can be stamped against it right now."),

  // A4. REWRITTEN. The bundle promises local verification of a regular's code,
  // which the provisional-accept decision removed: no counter device holds
  // code-minting or verification material, so everyone is accepted on trust
  // and validated at sync. The old line promised something the counter
  // deliberately does not do. See docs/design-copy-drift.md.
  // THE COUNTER SAYS WHAT HAPPENED, AND WHAT HAPPENED IS NOTHING.
  //
  // These four replaced three strings that told the customer their stamp was
  // kept when it was discarded:
  //
  //   offline.saved  "Saved. We will confirm this shortly."   <- rendered
  //   offline.body   "Every stamp is saved here and confirmed when we are back."
  //   offline.title  "Working offline · {held} held"
  //
  // There is no device-side queue. No localStorage, no IndexedDB, and the
  // service worker returns early on anything that is not a GET, so the earn
  // POST is never seen. The stamp was destroyed and a named person, standing
  // at a till, was told their purchase had been recorded.
  //
  // The last two were not even rendered: dead copy describing a capability
  // that does not exist, waiting to be wired up and believed. They are deleted
  // rather than kept for the queue that is coming, because a true-sounding
  // string with nothing behind it is the thing this project keeps being caught
  // by. They can be written again when there is something to describe.
  //
  // A counter that fails visibly is strictly safer than one that fails
  // silently: the staff member retries, the customer watches it happen, and
  // nobody is told a false thing.
  // --- the queue -----------------------------------------------------------
  //
  // ONE SCREEN, TWO READERS. The till is not a merchant surface with a
  // customer surface somewhere else: the customer is standing on the other
  // side of it, reading the same words, having just watched nothing happen.
  // Every line here is written to be true to both of them at once.
  //
  // NOTHING FROM THE CONFIRMATION FAMILY. No saved, no recorded, no kept. The
  // first two are what was removed yesterday; "kept" is one synonym away and
  // was rejected for that reason.
  //
  // AND NO PROMISE OF OUTCOME. An earlier draft said "will send when the
  // connection is back", which with a six-hour ceiling is sometimes false. "It
  // sends when the connection returns" states the mechanism; the countdown
  // beside it carries the condition. A softer false promise replacing a hard
  // one is the same error at lower volume.
  "queue.waiting": S("Waiting to send · {count} · {remaining} left"),
  "queue.notSent": S("Not sent yet"),
  // "On this device only" does the customer's work in four words: not lost,
  // not confirmed, and the reason showing the code again is pointless.
  "queue.onDevice": S(
    "This stamp is on this device only. It sends when the connection returns.",
  ),
  // The ruled wording had an em-dash, which the house rule forbids and a test
  // enforces. Split, not reworded: the dash was joining two independent
  // clauses, which is the case a full stop handles without argument. Third
  // time, after A3 and B5.
  "queue.dontShowAgain": S("Don't show your code again. It's already on this device."),
  // Merchant-only, and it is meant to alarm. This is the sentence that makes
  // somebody go and look at the router.
  "queue.urgent": S(
    "{count} stamps will be lost in {remaining} unless this device gets a connection.",
  ),

  // THE CLOSE WARNING IS THE PRIMARY CONTROL, not a courtesy.
  //
  // With six hours as the deadline and no Background Sync on iOS, a till
  // closed with a queue is a queue that never drains. The case is not exotic:
  // a shop whose connection drops at 20:00 and whose staff close at 22:00
  // loses everything when the tablet reopens at 08:00. That is a Tuesday.
  "queue.closing.title": S("{count} stamps have not been sent"),
  "queue.closing.body": S(
    "They are on this device only. If you close this now they will be lost, "
    + "and the customers will not get them.",
  ),
  "queue.closing.stay": S("Stay open"),
  "queue.closing.leave": S("Close anyway and lose them"),

  // THE WALL, WHICH IS NOT A STAGE OF THE COUNTDOWN.
  //
  // At forty held entries the till stops accepting stamps. That is a different
  // and worse event than the six hours expiring: it happens at the counter, in
  // front of a customer, mid-transaction, and there is no held-stamp
  // consolation because there is no stamp. It can arrive at any point on the
  // clock, including with four hours left, so it is a wall the escalation can
  // run into sideways rather than a stage of it.
  //
  // The existing E_OFFLINE_LIMIT pair is the SERVER refusing an entry at sync,
  // and it does not fit here. "The queue is full" uses our word for it, and a
  // customer standing in a queue reads that as something else entirely; "Get
  // back online before taking more" is an instruction to staff that a customer
  // hears as the shop being told off, and it says nothing to them at all.
  //
  // TWO READERS, AND THE CUSTOMER'S QUESTION IS "DID I LOSE IT". They offered a
  // code and watched nothing happen. The true answer is that nothing was taken
  // and their code was not spent.
  //
  // THE SECOND SENTENCE USED TO PROMISE A REMEDY AND IT IS GONE. It read "Take
  // their number and stamp it when this till is back online", which is a
  // promise the product cannot keep at the scale this state implies:
  //
  //   * The mechanism is real. `POST /counter/override-earn` records a
  //     codeless earn behind a manager PIN, and PHONE_DEAD and TABLET_FAILED
  //     are two of its four reason codes.
  //   * It is capped at `manager_overrides_per_window` per manager per
  //     `staff_rolling_window_hours` — five per twelve hours in the seed — and
  //     that budget is shared with reversals. The wall it would remedy is
  //     hit at forty held stamps and refuses every customer after, so the
  //     remedy is bounded at five while the event is unbounded.
  //   * It cannot enrol. A first-time customer has no membership, so
  //     override-earn refuses with E_MEMBER_NOT_FOUND. The person least able
  //     to be recovered is the one you least want to fail.
  //
  // So it says only what is true. Ending without a remedy is a worse product
  // than one with a remedy, and a better one than a sentence we cannot keep.
  // We spent this week deleting three of those; writing a fourth in the same
  // breath as the fix would have been the whole lesson missed.
  //
  // AND IT ADDRESSES THE CUSTOMER, NOT THE STAFF ABOUT THE CUSTOMER. The
  // earlier draft said "their code" and "take their number", which discusses
  // somebody in the third person while they stand there reading it, at the
  // moment something has just gone wrong for them. Every sibling state is
  // written to both readers at once; this one is now too.
  //
  // The merchant line sits below the customer's, per the ruling on placement.
  // The test for it is not "will the customer see this" — they will — but "is
  // it fine if they do". It is: it is true, it is specific, and it explains
  // that the shop is holding a lot of stamps rather than failing to care.
  "queue.full.title": S("This till is not taking stamps right now"),
  "queue.full.body": S(
    "Nothing was recorded and your code was not used. This till takes stamps "
    + "again when it reconnects.",
  ),
  // TRIMMED AFTER THE BODY CHANGED, and flagged rather than done quietly.
  // This line was approved as written, ending "It starts taking them again
  // when it reconnects." The body then lost its remedy and gained that exact
  // fact, so the two lines said the same thing one under the other. What is
  // left is the half only staff need: the number, and that the number is the
  // ceiling.
  "queue.full.merchant": S(
    "{count} stamps are waiting on this device, which is as many as it can hold.",
  ),

  // --- passkeys -------------------------------------------------------------
  //
  // THE WORD WAS THE DEFECT, AND THIS COMMENT USED TO ARGUE FOR IT.
  //
  // It said "Face ID" is what the customer sees and a passkey is what it is,
  // and that the credential is the same thing whichever sensor unlocks it.
  // The second half is true and the first half is a claim we cannot support.
  //
  // WebAuthn's user-verification flag says only that the authenticator
  // verified SOMEBODY BY SOME MEANS IT SUPPORTS. Face ID, Touch ID, a PIN and
  // a four-digit passcode are indistinguishable in it, and no parameter in the
  // specification can demand a biometric. So "Face ID" is unprovable at every
  // setting -- not at preferred, not at required, not behind a proxy, not
  // after an upgrade. A device passcode satisfies it exactly as well.
  //
  // A cycle was spent engineering toward a sentence that cannot be earned:
  // first a configuration hunt, then a proxy that would have rewritten the
  // ceremony -- and that proxy, applied to authentication only, would have
  // enrolled credentials on authenticators that cannot verify and locked those
  // customers out permanently the moment verification was demanded.
  //
  // "Passkey" is accurate, it is what every other product calls it, and it
  // cost nothing.
  //
  //   A capability that cannot be proven should not be named. The engineering
  //   that follows a wrong name is spent buying a sentence.
"passkey.title": S("Use a passkey next time"),
  "passkey.body": S(
    "Open your card without waiting for a code. It works on a new phone too, "
    + "because your passkey moves with your Apple or Google account.",
  ),
  "passkey.confirm": S("Use a passkey"),
  "passkey.later": S("Not now"),
  // Not an error. Declining is a choice, and the app simply carries on.
  // THREE CAUSES, THREE SENTENCES, AND THE OLD ONE NAMED A CAUSE NOBODY TESTED.
  //
  // It read "That did not work on this device", and the cause was not the
  // device: our code read the WebAuthn options from the wrong response, so it
  // failed identically on every device and would have tomorrow too. A customer
  // was being told their phone was at fault while the fault was ours.
  //
  // One message for three unrelated causes also cannot distinguish any of them,
  // which is the diagnostic equivalent of a control that always fires. So each
  // of these is said only where it has actually been checked.
  //
  // The reassurance survived on purpose. It is the half that was right: it
  // turns a dead end into a detour.
  "passkey.noAuthenticator": S(
    "This phone cannot create a passkey. Your card is fine and codes still work.",
  ),
  "passkey.failed": S(
    "That did not finish. Your card is fine and codes still work.",
  ),
  // Shown under the failure, small. Not an apology and not an explanation --
  // a handle, so a customer who tells us about it gives us the one thing that
  // makes it findable.
  "passkey.reference": S("Reference {ref}"),

  // --- finish setting up -----------------------------------------------------
  //
  // ON THE HOME SCREEN, NOT IN A PROFILE NOBODY OPENS. Each row disappears the
  // moment it is done, so a finished customer sees nothing at all rather than a
  // permanent checklist.
  //
  // THE EMAIL ROW WAITS. A customer with three stamps will not bother; a
  // customer two stamps from a free coffee will. So it appears only when a card
  // is close to a reward, and it is framed as protecting what they have rather
  // than as an account requirement, because it is not one.
  "profile.title": S("Finish setting up"),
  "profile.name.title": S("Add your name"),
  "profile.name.body": S("So staff can greet you at the counter."),
  "profile.passkey.title": S("Use a passkey"),
  "profile.passkey.body": S("Open your card without waiting for a code."),
  "profile.email.title": S("Add an email"),
  "profile.email.body": S("So you don't lose your stamps if you change phone."),

  // THE EMAIL SCREEN ITSELF. Same promise as the row that opens it, because a
  // customer who tapped "So you don't lose your stamps" and then met different
  // words would reasonably wonder what they had actually agreed to.
  //
  // Nothing here says "we've sent you a link", because nothing is sent: there
  // is no mail sender configured. Copy that describes a message the customer
  // will then wait for and never receive is worse than no copy at all, and it
  // is the kind of sentence that gets written once and outlives the reason.
  "email.title": S("Add an email"),
  "email.body": S("So you don't lose your stamps if you change phone."),
  "email.label": S("Email"),
  "email.save": S("Save"),
  "email.notNow": S("Not now"),
  "email.invalid": S("That does not look like an email address."),
  "email.failed": S("That did not save. Try again."),

  // THE CAP REFUSING IS THE CAP WORKING, and it must not read as a broken app.
  //
  // A message to Qatar costs 0.2634 USD, so the send rate is held deliberately
  // low. When it bites, the customer was seeing the generic error — which is
  // the same defect as the counter's calm grey "Saved" panel: a true state
  // rendered as the wrong one, and a person concluding the product does not
  // work.
  //
  // It says wait rather than retry, because retrying is exactly what will not
  // help, and it does not apologise or blame the customer for asking twice.
  "auth.tooManyCodes": S(
    "Too many codes have been sent just now. Wait a few minutes and try again.",
  ),

  "offline.notSaved": S("Not saved. There is no connection."),
  "offline.notSavedBody": S(
    "Nothing was recorded. Try again in a moment, or take their number and "
    + "stamp it when the connection is back.",
  ),
  "offline.redeem": S("Redemptions need a connection too."),

  "E_OFFLINE_LIMIT.title": S("The queue is full"),
  "E_OFFLINE_LIMIT.body": S(
    "This till is holding {max_entries} stamps already. Get back online before taking more.",
  ),

  "E_REDEEM_OFFLINE.title": S("Rewards need a connection"),
  // CORRECTED 2026-09-18. It said "Hold this one until we are back", the very
  // phrasing services/redemption.py forbids because it reads as a queue, and a
  // queued reward is one that can be given twice.
  "E_REDEEM_OFFLINE.body": S("Stamps still work. Ask them to come back for it when this till is online."),

  // A5.
  "undo.title": S("Undo this stamp?"),
  // CORRECTED 2026-09-17, for the same pronoun. "Is told" is also narrower
  // than it sounds: nothing is sent; the correction is on their Activity.
  "undo.body": S("{name} goes back to {balance} of {threshold}. Their Activity shows the correction."),
  "undo.confirm": S("Undo"),
  "undo.keep": S("Keep it"),
  // CORRECTED 2026-09-17: "{name} was told". Nothing is sent to the customer;
  // the correction is on their Activity the next time they open the app.
  "undo.done": S("Undone. The correction shows on {name}'s Activity."),
  // E_NOT_REVERSIBLE MEANS "NOT A STAMP" (reversal.py). Until 2026-09-18 it
  // carried E_MANAGER_REQUIRED's sentence, and the till showed it for both.
  "E_MANAGER_REQUIRED.title": S("This one needs a manager"),
  "E_MANAGER_REQUIRED.body": S("The three minute window has passed. A manager can still undo it."),
  "E_NOT_REVERSIBLE.title": S("Only a stamp can be undone here"),
  "E_NOT_REVERSIBLE.body": S("That entry is not a stamp. Nothing was undone."),

  /* EVERY REFUSAL AN UNDO OR A REWARD CAN GET, WITH WORDS (2026-09-18). Of the
   * undo's eleven, the till had words for one; the rest said "Something went
   * wrong here. Try once more", and trying once more was refused again. The
   * end-to-end test met it on its first undo. apps/counter/prove-refusals.mjs
   * now fails on any refusal the till can receive with no words. */
  // The till's sentence and the server's differ: a till built from other copy
  // than the one on file. Found by the proof above the day it could look.
  "E_CONSENT_WORDING_MISMATCH.title": S("This till's sentence is out of date"),
  "E_CONSENT_WORDING_MISMATCH.body": S("The line it asks customers to agree to is not the one on file, so nobody was added. Tell Damga."),
  "E_ENTRY_UNKNOWN.title": S("That stamp is not on record"),
  "E_ENTRY_UNKNOWN.body": S("Nothing was undone."),
  "E_ALREADY_REVERSED.title": S("Already undone"),
  "E_ALREADY_REVERSED.body": S("That stamp was undone already. Nothing changed."),
  "E_NOT_AUTHORISED.title": S("Not on this shop's team"),
  "E_NOT_AUTHORISED.body": S("Only this shop's own people can do this here. Nothing changed."),
  "E_STAFF_LOOKUP_FAILED.title": S("We could not check who is signed in"),
  "E_STAFF_LOOKUP_FAILED.body": S("Nothing was recorded. Try again in a minute."),
  "E_NOT_YOURS.title": S("Only the person who stamped it"),
  "E_NOT_YOURS.body": S("For three minutes, whoever made a stamp can undo it. After that, a manager can."),
  "E_REASON_REQUIRED.title": S("This needs a manager's reason"),
  "E_REASON_REQUIRED.body": S("The three minutes had passed, so undoing it is a manager's decision with a reason on the record. This till has nowhere to give one yet: tell Damga which stamp."),
  "E_REASON_UNKNOWN.title": S("That reason is not on the list"),
  "E_REASON_UNKNOWN.body": S("Nothing was undone."),
  "E_TOO_OLD.title": S("Too long ago to undo here"),
  "E_TOO_OLD.body": S("After a day, a stamp is undone by Damga. Tell us which one."),
  "E_REWARD_ALREADY_USED.title": S("That reward was already handed over"),
  "E_REWARD_ALREADY_USED.body": S("Nothing changed. If something is wrong, a manager can look at the card."),
  "E_OVERRIDE_CAP.title": S("No more undos for now"),
  "E_OVERRIDE_CAP.body": S("A manager has undone as many stamps as allowed for the moment. Damga can look at it."),
  "E_TILL_CAP.title": S("This shop has all its tills"),
  "E_TILL_CAP.body": S("Every till this shop may register is registered. Damga can help."),
  "E_NO_REWARD_READY.title": S("Nothing to give yet"),
  "E_NO_REWARD_READY.body": S("This card has no reward ready here. Stamp it as usual."),
  "E_CODE_REQUIRED.title": S("Their code, please"),
  "E_CODE_REQUIRED.body": S("A reward is given against the customer's six digits, read out here."),
  "E_CODE_NOT_THIS_CARD.title": S("That code is for a different card"),
  "E_CODE_NOT_THIS_CARD.body": S("Ask them to read the code from their own app. Nothing was given."),
  "E_REWARD_UNKNOWN.title": S("No reward like that here"),
  "E_REWARD_UNKNOWN.body": S("Nothing was given."),
  "E_REWARD_VOID.title": S("That reward was undone"),
  "E_REWARD_VOID.body": S("The stamp that earned it was undone, so there is nothing to give."),
  "E_REWARD_LAPSED.title": S("That reward has lapsed"),
  "E_REWARD_LAPSED.body": S("Its time ran out, so it cannot be given."),

  // Generic, and deliberately not a stack trace.
  "error.generic.title": S("Something went wrong here"),
  "error.generic.body": S("Nothing was recorded. Try once more."),
  "error.offline.title": S("No connection"),
  "error.session.title": S("Signed out"),
  "error.session.body": S("Sign in again to see your cards."),

  "common.retry": S("Try again"),
  "common.close": S("Close"),
  "common.loading": S("Loading"),
} as const;

export type CopyKey = keyof typeof COPY;

/** Fill {placeholders}. Anything unfilled is left visible rather than blanked,
 *  because a missing parameter should look wrong in review, not read as
 *  finished copy with a hole in it. */
export function t(
  key: CopyKey,
  lang: Lang = "en",
  params: Record<string, string | number> = {},
): string {
  const entry = COPY[key];
  const raw = (lang === "ar" ? entry.ar : entry.en) ?? entry.en;
  return raw.replace(/\{(\w+)\}/g, (whole, name) =>
    name in params ? String(params[name]) : whole,
  );
}

/**
 * A plural-aware lookup: `plural("wallet.count", 1)` picks `wallet.count.one`.
 *
 * Uses Intl.PluralRules rather than `n === 1`, because Arabic has six
 * categories and the shape has to exist before the Arabic copy can be written
 * into it. English resolves to two of the six; the rest are present and
 * currently identical, which is honest about what has been decided.
 */
export function plural(
  base: string,
  n: number,
  lang: Lang = "en",
  params: Record<string, string | number> = {},
): string {
  const category = new Intl.PluralRules(lang === "ar" ? "ar" : "en").select(n);
  const key = `${base}.${category}` as CopyKey;
  const fallback = `${base}.other` as CopyKey;
  return t(key in COPY ? key : fallback, lang, { n, ...params });
}

/** Which keys still have no Arabic at all. Reported rather than hidden. */
export function missingArabic(): CopyKey[] {
  return (Object.keys(COPY) as CopyKey[]).filter((k) => COPY[k].ar === null);
}

/**
 * Which keys have Arabic that no native speaker has read.
 *
 * A different question from `missingArabic()`, and the more dangerous one: a
 * missing string falls back to English and is obvious, while an unreviewed one
 * renders like any other and is obvious to nobody but the person reading it in
 * Arabic, who is the customer.
 */
export function unreviewedArabic(): CopyKey[] {
  return (Object.keys(COPY) as CopyKey[]).filter((k) => COPY[k].review?.state === "drafted");
}

/**
 * Copy written here rather than taken from the design bundle, awaiting the
 * founder's read.
 *
 * A prefix rather than a list of keys, because the point is the screen: every
 * word Activity says is mine, and a key added to that block tomorrow inherits
 * the flag instead of quietly escaping it. `activity.title` is the exception
 * and is excluded by name, being the one string the bundle's own table
 * carries.
 *
 * Nothing here blocks building. `scripts/copy-check.mjs` is what stops it
 * reaching a customer, and clearing the flag is deleting a line from this
 * array once the words have been read.
 */
export const PENDING_APPROVAL: readonly {
  /** A whole screen is mine. Anything added under it inherits the flag. */
  prefix?: string;
  /** Named keys, where a screen is mostly the bundle's and partly mine. */
  keys?: readonly string[];
  except?: readonly string[];
  because: string;
}[] = [
  { prefix: "favs.", because: "Favourites, kept on the device; the artboard's empty state, reworded for what can actually be saved today." },
  { prefix: "discover.", because: "Discover with nothing listed: the artboard drew the empty state, these are its words for today." },
  { prefix: "updates.", because: "The updates sheet, whose notices are worked out on the phone because Damga sends nothing." },
  { prefix: "notice.", because: "The notices themselves, derived from the customer's own cards." },
  { prefix: "export.", because: "Download your data, built against GET /me/export on 2026-09-17." },
  {
    prefix: "merchantSignIn.",
    because:
      "The merchant's username-and-password sign-in on the till's kiosk, ruled by the "
      + "founder on 2026-09-18. No artboard draws it; the intake artboard's password "
      + "screen was for self-registration, which does not exist.",
  },
  {
    keys: [
      "counter.endShift", "detail.sameEverywhere",
      "E_CONSENT_VERSION_DRAFT.title", "E_CONSENT_VERSION_DRAFT.body",
      "E_CONSENT_VERSION_MISSING.title", "E_CONSENT_VERSION_MISSING.body",
      "E_CONSENT_VERSION_SYNTHETIC.title", "E_CONSENT_VERSION_SYNTHETIC.body",
    ],
    because: "counter.endShift was hard-coded in the till's JSX and moved here unchanged; "
      + "detail.sameEverywhere labels the code on the card, ordered by the brief of 2026-09-18; "
      + "the E_CONSENT_VERSION_* words give the consent gate a sentence at the till, which it never had.",
  },
  {
    keys: [
      "counter.giveReward", "counter.giveEnter", "counter.giveBack", "counter.giveNow",
      "counter.giveNowWhat", "counter.given", "counter.givenPlain", "counter.givenBody",
      "E_MANAGER_REQUIRED.title", "E_MANAGER_REQUIRED.body",
      "E_NOT_REVERSIBLE.title", "E_NOT_REVERSIBLE.body", "E_REDEEM_OFFLINE.body",
      "E_ENTRY_UNKNOWN.title", "E_ENTRY_UNKNOWN.body", "E_ALREADY_REVERSED.title", "E_ALREADY_REVERSED.body",
      "E_NOT_AUTHORISED.title", "E_NOT_AUTHORISED.body", "E_STAFF_LOOKUP_FAILED.title", "E_STAFF_LOOKUP_FAILED.body",
      "E_NOT_YOURS.title", "E_NOT_YOURS.body", "E_REASON_REQUIRED.title", "E_REASON_REQUIRED.body",
      "E_REASON_UNKNOWN.title", "E_REASON_UNKNOWN.body", "E_TOO_OLD.title", "E_TOO_OLD.body",
      "E_REWARD_ALREADY_USED.title", "E_REWARD_ALREADY_USED.body", "E_OVERRIDE_CAP.title", "E_OVERRIDE_CAP.body",
      "E_TILL_CAP.title", "E_TILL_CAP.body", "E_NO_REWARD_READY.title", "E_NO_REWARD_READY.body",
      "E_CODE_REQUIRED.title", "E_CODE_REQUIRED.body", "E_CODE_NOT_THIS_CARD.title", "E_CODE_NOT_THIS_CARD.body",
      "E_REWARD_UNKNOWN.title", "E_REWARD_UNKNOWN.body", "E_REWARD_VOID.title", "E_REWARD_VOID.body",
      "E_REWARD_LAPSED.title", "E_REWARD_LAPSED.body",
      "E_CONSENT_WORDING_MISMATCH.title", "E_CONSENT_WORDING_MISMATCH.body",
      "lookup.scope",
      "activity.REWARD_ISSUE.voided", "activity.REWARD_ISSUE.voidedDetail", "activity.EARN_REVERSAL.withReward",
    ],
    because: "Giving a reward at the till, and a sentence for every refusal an undo or a reward can get "
      + "(2026-09-18). The till could not hand over a reward at all, and had words for one of the undo's "
      + "eleven refusals; both found by the end-to-end test. No artboard draws them.",
  },
  {
    prefix: "enrolEmail.",
    because:
      "An email at the till (047, 2026-09-18), so a till-made card is there when the "
      + "customer signs in. No artboard draws it: the artboards predate SMS being parked.",
  },
  {
    prefix: "shell.",
    because:
      "The customer app rebuilt from the light artboard on 2026-09-17. Its own "
      + "words where they were true; these where the artboard named something "
      + "the product does not have. Every one checked against the code.",
  },
  { prefix: "home.", because: "The Home screen of the same rebuild; see shell." },
  { prefix: "detail.", because: "The card detail of the same rebuild; see shell." },
  { prefix: "sheet.", because: "The sheets of the same rebuild; see shell." },
  { prefix: "seal.", because: "The seal and its labels, same rebuild; see shell." },
  { prefix: "toast.", because: "Toasts on real events only, same rebuild; see shell." },
  { prefix: "who.", because: "Who sees what, verified against the counter's responses; see shell." },
  { prefix: "signout.", because: "The sign-out sheet, same rebuild; see shell." },
  {
    keys: [
      "settings.security", "settings.yourData", "settings.whoSees", "settings.whoSeesBody",
      "settings.delete", "settings.deleteBody", "settings.noName",
      "activity.group.today", "activity.group.yesterday", "activity.group.week",
      "activity.group.earlier", "wallet.readyCount.one", "wallet.readyCount.other",
      "wallet.readyCount.zero", "wallet.readyCount.two", "wallet.readyCount.few",
      "wallet.readyCount.many", "time.today", "time.yesterday", "time.daysAgo",
    ],
    because: "Settings and Activity rows added in the same rebuild; see shell.",
  },
  {
    prefix: "passkey.",
    because:
      "No artboard draws a passkey offer, because the bundle predates the SMS "
      + "price that makes passkeys the difference between a customer's new "
      + "phone costing a riyal and costing nothing.",
  },
  {
    prefix: "profile.",
    because:
      "The home-screen setup card. B2 draws a wallet and not the rows above "
      + "it, and the email row's framing is a judgement about when to ask "
      + "rather than a string from the bundle.",
  },
  {
    keys: ["auth.tooManyCodes"],
    because:
      "Written when the SMS price made the rate limit real. No artboard draws "
      + "a rate-limited sign-in, because until the price was known nobody "
      + "expected the cap to be reachable.",
  },
  {
    prefix: "activity.",
    except: ["activity.title"],
    because:
      "Activity has no artboard. Every string is the author's, including the " +
      "sentence that tells a customer a stamp was taken back.",
  },
  // `queue.` WAS HERE AND IS NOT ANY MORE. Ruled twice: the first eight
  // strings in the brief that settled the six-hour ceiling, and `queue.full.*`
  // in the one that ruled the fifth state. Leaving a declaration standing over
  // copy that has been approved is the same defect as the opposite — a gate
  // reporting a state that is not true — and it teaches people to read the
  // gate's output as noise.
  //
  // None of them carries Arabic, so they fall back to English, which is the
  // safe state rather than an unreviewed claim.
  {
    keys: ["auth.providerCancelled", "auth.providerLost", "auth.providerFailed"],
    because:
      "The bundle drew Apple, Google and Microsoft as buttons that simply "
      + "work, so nothing draws one that does not. These three name the three "
      + "ways the trip back can fail, and telling them apart is the whole "
      + "point: only the last is ours.",
  },
  {
    keys: ["activity.recordedLate"],
    because: "A stamp appearing in the past in an append-only ledger.",
  },
  {
    prefix: "card.closing.",
    because:
      "A card whose merchant is closing has no artboard. B5 draws the " +
      "aftermath; nothing draws the notice period.",
  },
  {
    // Named rather than by prefix: most of Archive is B5's own copy and only
    // these four are invented, so flagging the prefix would ask the founder to
    // re-approve their own words.
    keys: [
      "archive.closedOnly.title",
      "archive.closedOnly.body",
      "archive.empty.title",
      "archive.empty.body",
    ],
    because:
      "B5 draws an Archive holding dormant cards. Dormancy is not built, so " +
      "today it holds closed shops only and needed a heading that is true, " +
      "plus an empty state B5 does not draw.",
  },
];

/** The keys a person still has to read before this ships. */
export function pendingApproval(): CopyKey[] {
  return (Object.keys(COPY) as CopyKey[]).filter((k) =>
    PENDING_APPROVAL.some(
      (rule) =>
        !(rule.except ?? []).includes(k) &&
        ((rule.prefix !== undefined && k.startsWith(rule.prefix)) ||
          (rule.keys ?? []).includes(k)),
    ),
  );
}
